'use client';

import {
  IconChevronRight,
  IconInbox,
  IconMessage2,
  IconTrash,
} from '@tabler/icons-react';

import { Button, Chip, Container, Modal, VStack } from '@j-s/components';
import { ClientSavedTicket, ContactMessageStatus } from '@j-s/dr-shared';

export interface MyTicketsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  tickets: ClientSavedTicket[];
  onOpenTicket: (ticketId: string, accessToken?: string) => void;
  onRemoveTicket: (ticketId: string) => void;
}

const STATUS_CHIPS: Record<
  ContactMessageStatus,
  { label: string; color: 'danger' | 'warning' | 'success' | 'default' }
> = {
  unread: { label: 'Wysłane', color: 'danger' },
  phone_call: { label: 'Telefon', color: 'warning' },
  read: { label: 'Weryfikacja', color: 'warning' },
  in_progress: { label: 'W trakcie', color: 'warning' },
  replied: { label: 'Odpowiedź', color: 'success' },
  client_read: { label: 'Odczytana', color: 'warning' },
  archived: { label: 'Archiwum', color: 'default' },
  closed: { label: 'Zamknięte', color: 'default' },
};

export const MyTicketsDrawer = ({
  isOpen,
  onClose,
  tickets,
  onOpenTicket,
  onRemoveTicket,
}: MyTicketsDrawerProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="md"
      title={
        <div className="flex items-center gap-3.5 w-full">
          <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
            <IconInbox size={20} />
          </div>
          <div className="min-w-0 flex-1 flex flex-col justify-center gap-0.5">
            <h3 className="font-bold text-gray-900 text-base leading-tight truncate">
              Moje wiadomości
            </h3>
            <span className="text-xs text-gray-400 font-medium">
              Zapisane w tej przeglądarce
            </span>
          </div>
        </div>
      }
      body={
        <Container fullWidth>
          {tickets.length === 0 ? (
            <div className="py-10 text-center flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                <IconMessage2 size={24} />
              </div>
              <div className="text-sm font-semibold text-gray-700">
                Brak zapisanych wiadomości
              </div>
              <div className="text-xs text-gray-400 max-w-xs">
                Wysłane z tej przeglądarki wiadomości kontaktowe pojawią się
                tutaj automatycznie.
              </div>
            </div>
          ) : (
            <div className="max-h-[380px] overflow-y-auto pr-1 w-full">
              <VStack gap={2.5} fullWidth>
                {tickets.map((t) => {
                  const chip = STATUS_CHIPS[t.lastStatus] || {
                    label: t.lastStatus,
                    color: 'default' as const,
                  };

                  return (
                    <div
                      key={t.id}
                      onClick={() => onOpenTicket(t.id, t.accessToken)}
                      className="p-3.5 rounded-xl border border-gray-200 bg-gray-50/70 hover:bg-white hover:border-red-300 hover:shadow-xs transition-all cursor-pointer flex items-center justify-between gap-3 group"
                    >
                      <div className="space-y-1 flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-xs text-red-600">
                            {t.ticketNumber}
                          </span>
                          <Chip size="sm" variant="soft" color={chip.color}>
                            {chip.label}
                          </Chip>
                          <span className="text-[11px] text-gray-400">
                            {new Date(t.createdAt).toLocaleDateString('pl-PL')}
                          </span>
                        </div>
                        <div className="text-xs font-semibold text-gray-800 truncate">
                          {t.subject}
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveTicket(t.id);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Usuń z pamięci przeglądarki"
                        >
                          <IconTrash size={15} />
                        </button>
                        <IconChevronRight
                          size={18}
                          className="text-gray-400 group-hover:text-red-600 group-hover:translate-x-0.5 transition-all"
                        />
                      </div>
                    </div>
                  );
                })}
              </VStack>
            </div>
          )}
        </Container>
      }
      footer={
        <Button variant="secondary" size="sm" onPress={onClose}>
          Zamknij
        </Button>
      }
    />
  );
};
